/**
 * `SetList` — the SET LIST sheet. `docs/design-direction.md` §8.4, `spec.md` §6.7,
 * §13.10 (`/queue`).
 *
 * The queue is a selector's set list: a cream sheet pulled out of a record jacket, hand
 * numbered down the left margin, with the tune that is playing marked in felt-tip and
 * the ones already played crossed off below a second downbeat rule. It is the **one
 * deliberately light screen** in a dark app, and the single documented exception to the
 * inversion cap — it may fill 80% of the viewport while it is open, but **nothing else
 * on screen is inverted while it is** (§5.5.6, §8.4).
 *
 * ## Why this composes `Sheet` + `Paper` rather than being one element
 *
 * `Sheet` (§9.8) owns the modal contract: `role="dialog"`, `aria-modal`, the halftone
 * scrim, the focus trap, Escape, Telegram's BackButton, and the rise. `Paper` (§6.11)
 * owns the physical sheet: cream stock, the 28px `--cool-ink` ruling, the tape strips,
 * the hard-cut edge and the −0.8° rotation. They cannot be the same element, because
 * the rise and the dismiss drag animate `transform` and the rotation *is* a
 * `transform` — one element cannot carry both, and a paper surface that cannot rotate
 * must be dark instead (§12.14). So the panel is transparent and the object that rises
 * is the sheet of paper inside it.
 *
 * ## The four interactions, and the rule each one encodes
 *
 * **Tap** plays that item. **Swipe-left strikes; it never deletes.** A selector crosses
 * a tune off a list — the row keeps its slot, greys, draws a strike, and offers a mono
 * `ОТМЕНИТЬ` control for {@link UNDO_WINDOW_MS}. **The removal request is not issued
 * until that window closes**, so undo is not a compensating delete: it cancels a
 * request that was never sent (§8.4, §12.21).
 *
 * **Drag** reorders. The lift is 2px and a hard `--ink-offset`; no scale, no blur, no
 * tilt. The drop target is drawn as a 3px `--accent` bar — the downbeat mark — on the
 * row edge the item would land against. The local order is applied optimistically by
 * `store/queue`, which then adopts whatever the server's midpoint/renumber algorithm
 * returns rather than arguing with it. The same move is available from the keyboard:
 * the grab handle is a real button and ↑/↓ move the item one slot, because a
 * pointer-only reorder is not a reorder for a third of the audience.
 *
 * **Removing the item that is currently playing has a defined behaviour**, decided
 * here because §8.4 does not state one:
 *
 *  · Striking it changes **nothing** about playback. The strike is a local, reversible
 *    edit and the audio must survive an undo untouched — a needle that lifts and drops
 *    again on the same tune is a worse outcome than either alternative.
 *  · When the window closes and the removal commits, the tune comes off the deck as
 *    well as off the list: the set list advances to the item that followed it and plays
 *    it, and if there is nothing after it, playback stops. That is what "crossed off"
 *    means once it is final, and it is the only behaviour that leaves the sheet and the
 *    transport agreeing about what is playing.
 *  · **Closing the sheet cancels every pending strike** rather than committing it. The
 *    undo affordance only exists inside this sheet; a removal that fires after it is
 *    gone is a delete the user was never given the chance to take back.
 *
 * ## Copy
 *
 * `SET LIST`, `CLEAR`, `PLAYED` and `MORE` are English because they are label print and
 * panel chrome, which §11 licenses; every one of them carries a Russian
 * `aria-label`/accessible name, because the UI language is Russian. `ОТМЕНИТЬ` is
 * §8.4's own string and is set in the mono tier with the `cyr` modifier, which drops
 * the tracking and the forced case — tracked uppercase Cyrillic is banned (§12.16).
 * The empty state's string is owned by `Empty`'s `EMPTY_COPY.queue`, sentence-cased for
 * the same reason; this screen never re-types it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, KeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';

import { hasCyrillic } from '@bl/shared';

import type { ScreenProps } from '../app/router';
import { Empty } from '../app/states/Empty';
import { ErrorState } from '../app/states/ErrorState';
import { Loading } from '../app/states/Loading';
import { getWiring } from '../app/wiring';
import { DownbeatRule } from '../components/primitives/DownbeatRule';
import { buildPath, FALLBACK_ROUTE, navigate } from '../app/router';
import { Paper } from '../components/primitives/Paper';
import { cx } from '../components/primitives/Rule';
import { Sheet } from '../components/primitives/Sheet';
import { formatTimecode } from '../components/Scrub';
import { telegram } from '../lib/telegram';
import type { LocalQueueItem } from '../store/queue';
import { useQueueStore } from '../store/queue';

import styles from './setlist.module.css';

/* ══ copy and constants ════════════════════════════════════════════════════════════ */

/** The sheet header, in Display 800 caps. Label print, quoted as-is (§11). */
export const SET_LIST_TITLE = 'SET LIST';

/** Visible copy of the clear control — a word in a square outline, never a trash can. */
export const CLEAR_COPY = 'CLEAR';
/** Its accessible name. Every control has a Russian one (§9.4). */
export const CLEAR_LABEL = 'ОЧИСТИТЬ';

/** §8.4's undo control, verbatim. */
export const UNDO_COPY = 'ОТМЕНИТЬ';
/** Prefix of the undo control's accessible name: `Вернуть: <title>`. */
export const UNDO_LABEL_PREFIX = 'Вернуть';

/** The played-history heading, printed under the second downbeat rule. */
export const PLAYED_HEADING = 'PLAYED';
/** The control that expands the collapsed history. */
export const MORE_COPY = 'MORE';
export const MORE_LABEL = 'Показать всю историю';

/** Prefix of a grab handle's accessible name: `Переместить: <title>`. */
export const DRAG_LABEL_PREFIX = 'Переместить';

/**
 * How long a struck row waits before its removal is sent. §8.4 states 4 s, which is
 * long enough to read the strike and reach the control one-handed and short enough that
 * the sheet is not permanently full of undo buttons.
 */
export const UNDO_WINDOW_MS = 4000;

/** §8.4: the played history is collapsed to five with a `MORE` control. */
export const PLAYED_COLLAPSED = 5;

/**
 * Leftward travel that commits a strike. `--bar` (64px) — a full bar, and far enough
 * that a diagonal scroll cannot cross a tune off by accident.
 */
export const SWIPE_STRIKE_PX = 64;

/** Travel below which a pointer sequence is a tap, not a gesture. */
const TAP_SLOP_PX = 8;

/** Row skeletons drawn while the first read is in flight. Never a fabricated count. */
const SKELETON_ROWS = 6;

/* ══ types ═════════════════════════════════════════════════════════════════════════ */

/** The bound store hook, so a test can inject one built on a fake client. */
type QueueStoreHook = typeof useQueueStore;

/** An item plus its index in the store's `items`, which is what `select()` takes. */
interface Placed {
  readonly item: LocalQueueItem;
  readonly index: number;
}

/** A reorder in progress. Row geometry is measured once, at drag start. */
interface DragState {
  readonly itemId: number;
  readonly pointerId: number;
  /** Vertical midpoints of every *other* upcoming row, in DOM order. */
  readonly midpoints: readonly number[];
  /** Ids of the upcoming rows without the dragged one, in DOM order. */
  readonly rest: readonly number[];
  /** Slot in `rest` the item would land in. */
  to: number;
}

/** What the UI needs to know about a drag, without re-rendering on every pointer move. */
interface DragMark {
  readonly itemId: number;
  /** The row the dragged item lands *before*, or `null` for the end of the list. */
  readonly beforeItemId: number | null;
  /** The row the bar is drawn *under* when the item lands at the end. */
  readonly afterItemId: number | null;
}

/** The mark for a landing slot: a bar above `rest[to]`, or under the last row. */
function markFor(itemId: number, rest: readonly number[], to: number): DragMark {
  const beforeItemId = rest[to] ?? null;
  return {
    itemId,
    beforeItemId,
    afterItemId: beforeItemId === null ? (rest.at(-1) ?? null) : null,
  };
}

export interface SetListProps extends Partial<ScreenProps> {
  /** The queue slice. Defaults to the app-wide store. */
  readonly useQueue?: QueueStoreHook;
  /** Dismisses the sheet. Defaults to going back, which is what `/queue` is. */
  readonly onClose?: (() => void) | undefined;
  /** Plays the item at that index of `items`. Defaults to the app wiring. */
  readonly onPlay?: ((index: number) => void) | undefined;
  /** Stops playback — the last item was removed. Defaults to the app wiring. */
  readonly onStop?: (() => void) | undefined;
  /** The empty state's single action. No handler, no button (§8.7). */
  readonly onBrowse?: (() => void) | undefined;
  /** Overridable for tests; production always uses {@link UNDO_WINDOW_MS}. */
  readonly undoWindowMs?: number;
  /** Whether the transport is actually running — the marker is not a play state. */
  readonly playing?: boolean;
}

/* ══ helpers ═══════════════════════════════════════════════════════════════════════ */

/** The order `ids` would have if `itemId` were moved in front of `beforeItemId`. */
function moved(ids: readonly number[], itemId: number, beforeItemId: number | null): number[] {
  const rest = ids.filter((id) => id !== itemId);
  const at = beforeItemId === null ? rest.length : rest.indexOf(beforeItemId);
  const target = at < 0 ? rest.length : at;
  return [...rest.slice(0, target), itemId, ...rest.slice(target)];
}

function sameOrder(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** `Title, Performer` — a comma, because a dash here is the placeholder §12.6 bans. */
function accessibleName(item: LocalQueueItem): string {
  const performer = item.track.performer;
  return performer === null || performer.trim() === ''
    ? item.track.title
    : `${item.track.title}, ${performer}`;
}

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

export function SetList({
  useQueue = useQueueStore,
  onClose,
  onPlay,
  onStop,
  onBrowse,
  undoWindowMs = UNDO_WINDOW_MS,
  playing = false,
}: SetListProps = {}): JSX.Element {
  const state = useQueue();
  const { items, index, status, error } = state;

  /* Item ids whose removal is scheduled but not yet sent. */
  const [struck, setStruck] = useState<readonly number[]>([]);
  const [dragMark, setDragMark] = useState<DragMark | null>(null);
  const [showAllPlayed, setShowAllPlayed] = useState(false);

  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const dragRef = useRef<DragState | null>(null);
  const rowRefs = useRef(new Map<number, HTMLLIElement>());
  const swipeRef = useRef<{ id: number; x: number; y: number } | null>(null);
  const suppressClick = useRef(false);

  /* ── the two lists ──────────────────────────────────────────────────────────── */

  const placed = useMemo<Placed[]>(() => items.map((item, at) => ({ item, index: at })), [items]);
  const upcoming = useMemo(() => placed.filter((row) => !row.item.played), [placed]);
  const played = useMemo(() => placed.filter((row) => row.item.played), [placed]);

  /**
   * The header figures, computed from the rows actually left on the sheet — a struck
   * row is already off the list as far as the count and the runtime are concerned, and
   * both are real sums rather than the server's pre-strike figure (§12.6).
   */
  const live = upcoming.filter((placed) => !struck.includes(placed.item.id));
  const remainingS = live.reduce((total, placed) => total + placed.item.track.durationS, 0);
  const countLine = `${String(live.length)} · ${formatTimecode(remainingS)}`;

  /* ── defaults that reach the app's own wiring ───────────────────────────────── */

  const close = useCallback((): void => {
    if (onClose !== undefined) {
      onClose();
      return;
    }
    if (typeof window === 'undefined') return;
    /* Same guard the router's own BackButton uses: opened straight onto `/queue`
       there is nothing to go back to, and a bare `history.back()` is a dead press. */
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    navigate(buildPath(FALLBACK_ROUTE));
  }, [onClose]);

  const play = useCallback(
    (at: number): void => {
      if (onPlay !== undefined) {
        onPlay(at);
        return;
      }
      void getWiring().select(at);
    },
    [onPlay],
  );

  const stop = useCallback((): void => {
    if (onStop !== undefined) {
      onStop();
      return;
    }
    getWiring().pause();
  }, [onStop]);

  /* ── first read ─────────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (useQueue.getState().status === 'idle') void useQueue.getState().load();
  }, [useQueue]);

  /* ── the undo window ────────────────────────────────────────────────────────── */

  /**
   * Send the removal. Reads the store at fire time rather than closing over a render's
   * items: four seconds is long enough for the list to have changed underneath.
   */
  const commitRemoval = useCallback(
    (itemId: number): void => {
      timers.current.delete(itemId);
      setStruck((ids) => ids.filter((id) => id !== itemId));

      const before = useQueue.getState();
      const wasCurrent = before.items[before.index]?.id === itemId;

      void before.removeItem(itemId).then(() => {
        if (!wasCurrent) return;
        const after = useQueue.getState();
        /* `removeItem` leaves `index` on the successor, or `-1` on an empty set. */
        if (after.index >= 0 && after.items.length > 0) play(after.index);
        else stop();
      });
    },
    [play, stop, useQueue],
  );

  const commitRef = useRef(commitRemoval);
  useEffect(() => {
    commitRef.current = commitRemoval;
  }, [commitRemoval]);

  /* Closing the sheet abandons every pending strike — see the header. */
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const strike = useCallback(
    (itemId: number): void => {
      if (timers.current.has(itemId)) return;
      setStruck((ids) => (ids.includes(itemId) ? ids : [...ids, itemId]));
      timers.current.set(
        itemId,
        setTimeout(() => {
          commitRef.current(itemId);
        }, undoWindowMs),
      );
      telegram.haptics.impact('light');
    },
    [undoWindowMs],
  );

  const undo = useCallback((itemId: number): void => {
    const timer = timers.current.get(itemId);
    if (timer !== undefined) clearTimeout(timer);
    timers.current.delete(itemId);
    setStruck((ids) => ids.filter((id) => id !== itemId));
  }, []);

  /* ── reorder ────────────────────────────────────────────────────────────────── */

  const commitReorder = useCallback(
    (itemId: number, beforeItemId: number | null): void => {
      const ids = useQueue.getState().items.map((item) => item.id);
      if (sameOrder(ids, moved(ids, itemId, beforeItemId))) return;
      void useQueue.getState().reorder(itemId, beforeItemId);
      telegram.haptics.impact('light');
    },
    [useQueue],
  );

  /* `endDrag` is handed to pointer handlers that outlive a render; the commit it calls
     is read through a ref so it never fires a stale store's reorder. */
  const commitReorderRef = useRef(commitReorder);
  useEffect(() => {
    commitReorderRef.current = commitReorder;
  }, [commitReorder]);

  const endDrag = useCallback((commit: boolean): void => {
    const drag = dragRef.current;
    dragRef.current = null;
    setDragMark(null);
    /* Every exit path re-enables the gesture it took away — a drag that ends in a
       cancel must not leave the Mini App unable to dismiss itself (§7.3). */
    telegram.enableVerticalSwipes();
    if (drag === null || !commit) return;
    commitReorderRef.current(drag.itemId, drag.rest[drag.to] ?? null);
  }, []);

  const onHandleDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>, itemId: number): void => {
      const order = live.map((row) => row.item.id);
      const from = order.indexOf(itemId);
      if (from < 0) return;
      const rest = order.filter((id) => id !== itemId);
      const midpoints = rest.map((id) => {
        const row = rowRefs.current.get(id);
        if (row === undefined) return Number.POSITIVE_INFINITY;
        const rect = row.getBoundingClientRect();
        return rect.top + rect.height / 2;
      });

      dragRef.current = { itemId, pointerId: event.pointerId, midpoints, rest, to: from };
      setDragMark(markFor(itemId, rest, from));

      /* Or Telegram's own sheet-dismiss gesture steals the drag (§7.3, §10.1). */
      telegram.disableVerticalSwipes();
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [live],
  );

  const onHandleMove = useCallback((event: ReactPointerEvent<HTMLButtonElement>): void => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    let to = 0;
    for (const midpoint of drag.midpoints) {
      if (event.clientY > midpoint) to += 1;
    }
    if (to === drag.to) return;
    drag.to = to;
    setDragMark(markFor(drag.itemId, drag.rest, to));
  }, []);

  const onHandleKey = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, itemId: number): void => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      const order = live.map((row) => row.item.id);
      const from = order.indexOf(itemId);
      if (from < 0) return;
      const rest = order.filter((id) => id !== itemId);
      const to = event.key === 'ArrowUp' ? from - 1 : from + 1;
      if (to < 0 || to > rest.length) return;
      event.preventDefault();
      commitReorder(itemId, rest[to] ?? null);
    },
    [commitReorder, live],
  );

  /* ── tap vs swipe ───────────────────────────────────────────────────────────── */

  const onRowDown = useCallback((event: ReactPointerEvent<HTMLElement>, itemId: number): void => {
    suppressClick.current = false;
    swipeRef.current = { id: itemId, x: event.clientX, y: event.clientY };
  }, []);

  const onRowUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>, itemId: number): void => {
      const start = swipeRef.current;
      swipeRef.current = null;
      if (start === null || start.id !== itemId) return;

      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (dx <= -SWIPE_STRIKE_PX && Math.abs(dy) < Math.abs(dx)) {
        suppressClick.current = true;
        strike(itemId);
        return;
      }
      if (Math.abs(dx) > TAP_SLOP_PX || Math.abs(dy) > TAP_SLOP_PX) suppressClick.current = true;
    },
    [strike],
  );

  const onRowClick = useCallback(
    (at: number): void => {
      if (suppressClick.current) {
        suppressClick.current = false;
        return;
      }
      telegram.haptics.impact('light');
      play(at);
    },
    [play],
  );

  /* ── row ────────────────────────────────────────────────────────────────────── */

  function renderRow(row: Placed, hand: number | null): JSX.Element {
    const { item } = row;
    const isStruck = struck.includes(item.id);
    const isCurrent = row.index === index;
    const isDragging = dragMark?.itemId === item.id;
    const title = item.track.title;
    const performer = item.track.performer;
    const dropBefore = dragMark?.beforeItemId === item.id;
    const dropAfter = dragMark?.afterItemId === item.id;

    return (
      <li
        key={item.id}
        ref={(node) => {
          if (node === null) rowRefs.current.delete(item.id);
          else rowRefs.current.set(item.id, node);
        }}
        className={cx(styles.row, isStruck && styles.struck)}
        data-item-id={String(item.id)}
        data-struck={isStruck ? 'true' : undefined}
        data-current={isCurrent ? 'true' : undefined}
        data-pending={item.pending === true ? 'true' : undefined}
        data-dragging={isDragging ? 'true' : undefined}
        data-drop={dropBefore ? 'before' : dropAfter ? 'after' : undefined}
      >
        <span className={cx('scrawl', styles.num)} aria-hidden="true">
          {hand === null ? '' : hand}
        </span>

        <button
          type="button"
          className={styles.main}
          aria-label={accessibleName(item)}
          aria-current={isCurrent ? true : undefined}
          onPointerDown={(event) => {
            onRowDown(event, item.id);
          }}
          onPointerUp={(event) => {
            onRowUp(event, item.id);
          }}
          onPointerCancel={() => {
            swipeRef.current = null;
          }}
          onClick={() => {
            onRowClick(row.index);
          }}
        >
          <span className={styles.lines}>
            <span className={cx('t-ui', 't-17', 't-clamp-1', styles.title)}>
              {isCurrent && !isStruck ? <mark className={styles.marker}>{title}</mark> : title}
            </span>
            {performer === null || performer.trim() === '' ? null : (
              <span
                className={cx(
                  't-mono',
                  't-11',
                  't-clamp-1',
                  styles.meta,
                  hasCyrillic(performer) && styles.cyr,
                )}
              >
                {performer}
              </span>
            )}
          </span>
          <span className={cx('t-mono', 't-13', styles.duration)}>
            {formatTimecode(item.track.durationS)}
          </span>
        </button>

        {hand === null ? null : isStruck ? (
          <button
            type="button"
            className={cx('t-mono', 't-11', styles.undo, styles.cyr)}
            aria-label={`${UNDO_LABEL_PREFIX}: ${title}`}
            onClick={() => {
              undo(item.id);
            }}
          >
            {UNDO_COPY}
          </button>
        ) : (
          <button
            type="button"
            className={styles.handle}
            aria-label={`${DRAG_LABEL_PREFIX}: ${title}`}
            onPointerDown={(event) => {
              onHandleDown(event, item.id);
            }}
            onPointerMove={onHandleMove}
            onPointerUp={() => {
              endDrag(true);
            }}
            onPointerCancel={() => {
              endDrag(false);
            }}
            onLostPointerCapture={() => {
              endDrag(false);
            }}
            onKeyDown={(event) => {
              onHandleKey(event, item.id);
            }}
          >
            <span className={styles.dash} aria-hidden="true" />
            <span className={styles.dash} aria-hidden="true" />
            <span className={styles.dash} aria-hidden="true" />
          </button>
        )}
      </li>
    );
  }

  /* ── body ───────────────────────────────────────────────────────────────────── */

  const visiblePlayed = showAllPlayed ? played : played.slice(0, PLAYED_COLLAPSED);

  let body: JSX.Element;
  if (status === 'error' && error !== null && items.length === 0) {
    body = (
      <ErrorState
        failure={error}
        grid="sheet"
        onRetry={() => {
          void state.load();
        }}
      />
    );
  } else if (status === 'loading' && items.length === 0) {
    body = <Loading grid="sheet" rows={SKELETON_ROWS} />;
  } else if (items.length === 0) {
    /* `Empty` brings the sheet's own paper object (§8.7's best state), so this branch
       renders it *instead of* the ledger's paper — never both, or the screen would
       carry two inverted blocks at once (§12.13). */
    body = <Empty variant="queue" grid="sheet" onAction={onBrowse} />;
  } else {
    body = (
      <Paper as="section" tape={2} className={styles.paper}>
        <div className={styles.top}>
          <button
            type="button"
            className={cx('t-mono', 't-11', styles.clear)}
            aria-label={CLEAR_LABEL}
            onClick={() => {
              void state.clear();
            }}
          >
            {CLEAR_COPY}
          </button>
        </div>

        <ul className={styles.list}>{upcoming.map((placed, at) => renderRow(placed, at + 1))}</ul>

        {played.length === 0 ? null : (
          <div className={styles.played}>
            <DownbeatRule label={PLAYED_HEADING} level={3} />
            <ul className={styles.list}>
              {visiblePlayed.map((placed) => renderRow(placed, null))}
            </ul>
            {played.length <= PLAYED_COLLAPSED || showAllPlayed ? null : (
              <button
                type="button"
                className={cx('t-mono', 't-11', styles.more)}
                aria-label={MORE_LABEL}
                onClick={() => {
                  setShowAllPlayed(true);
                }}
              >
                {MORE_COPY}
              </button>
            )}
          </div>
        )}
      </Paper>
    );
  }

  return (
    <Sheet
      open
      title={SET_LIST_TITLE}
      count={items.length === 0 ? undefined : countLine}
      onClose={close}
      className={styles.sheet}
      bodyClassName={styles.body}
    >
      <div data-playing={playing ? 'true' : 'false'}>{body}</div>
    </Sheet>
  );
}
